import { camelCase } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { Person } from '@material-ui/icons';
import {
  ConfigFieldControl,
  FormElementPlugin,
  FormElementProps,
  useDataSourceProps,
  useFormContext
} from '@sierralabs/forms-ui';
import { GroupPicker, User, UserPicker } from '@sierralabs/user-ui';
import { useUpdateTask } from '@sierralabs/workflow-ui';

import { FormConfigPicker, FormPickerConfig } from './config/FormConfigPicker';

export interface FormUserPickerConfig extends FormPickerConfig {
  /**
   * Assigns the selected User to TaskV2 (`taskV2.user`).
   */
  assignToTask?: boolean;
  includeInactive?: boolean;
  hideSupportUsers?: boolean;
  filterGroupIds?: number[];
}

const FormUserPicker = <T extends object>({
  element,
  edit,
  disabled
}: FormElementProps<T, FormUserPickerConfig>) => {
  const {
    placeholder,
    multiple,
    assignToTask,
    includeInactive,
    hideSupportUsers,
    filterGroupIds
  } = element.config || {};
  const { entitySchema, formValues, setFormValue } = useFormContext<
    T,
    FormUserPickerConfig
  >();
  // taskId field is available for workflowEnabled entities
  const taskId: number | undefined = (formValues as any).taskId;
  const { updateTask } = useUpdateTask(taskId || 0);
  const key = (element.dataSource as keyof T) || element.key;
  const [value, setValue] = useState((formValues[key] as any) || undefined);
  const previousValueRef = useRef(value);
  const dataSourceProps = useDataSourceProps(element, value);

  // refresh value if formValues change outside of the component
  useEffect(() => {
    if (previousValueRef.current !== formValues[key]) {
      setValue(formValues[key]);
      previousValueRef.current = formValues[key];
    }
  }, [formValues, key]);

  return (
    <UserPicker
      multiple={multiple}
      disabled={edit || disabled}
      includeDeleted={includeInactive}
      hideSupportUsers={hideSupportUsers}
      filterGroupIds={filterGroupIds}
      value={value}
      textFieldProps={{
        label: undefined,
        error: dataSourceProps.error,
        helperText: dataSourceProps.helperText,
        placeholder: placeholder || 'Select User(s)'
      }}
      onChange={(value: User | User[] | undefined) => {
        // get column to check if array
        const column = entitySchema?.columns.find(column => {
          const name = column.name || camelCase(column.displayName);
          return element.dataSource === name;
        });
        // convert selected to array of users
        const users = Array.isArray(value) ? value : value ? [value] : [];
        const lastSelectedId =
          users.length > 0 ? users[users.length - 1].id : null;
        // if column is an array, make sure to save array of ids
        const ids = column?.array ? users.map(user => user.id) : lastSelectedId;
        setFormValue(key, ids as any);
        // apply last selected user id only (only 1 user assignee per task)
        if (assignToTask && taskId) {
          updateTask({ userId: lastSelectedId });
        }
      }}
    />
  );
};

export const FormConfigUserPicker = <FormValuesType extends object>() => {
  const { formState, setFormState, notifyFormEdit } = useFormContext<
    FormValuesType,
    FormUserPickerConfig
  >();
  const element = formState.selectedFormElement!;
  if (!element.config) element.config = {};
  return (
    <>
      <FormConfigPicker
        // disable multiple since task can only be assigned a single user
        disableMultiple={element.config?.assignToTask}
      />
      <ConfigFieldControl>
        <FormControlLabel
          label='Assign User to Task'
          control={
            <Checkbox
              color='primary'
              checked={!!element.config?.assignToTask}
              onChange={() => {
                element.config!.assignToTask = !element.config!.assignToTask;
                // set multiple config to false if true since
                // task can only be assign on a single user
                if (element.config!.assignToTask && element.config!.multiple) {
                  element.config!.multiple = false;
                }
                setFormState({ ...formState, selectedFormElement: element });
                notifyFormEdit();
              }}
            />
          }
        />
      </ConfigFieldControl>
      <ConfigFieldControl>
        <FormControlLabel
          label='Include Inactive Users'
          control={
            <Checkbox
              color='primary'
              checked={!!element.config?.includeInactive}
              onChange={() => {
                element.config!.includeInactive =
                  !element.config!.includeInactive;
                setFormState({ ...formState, selectedFormElement: element });
                notifyFormEdit();
              }}
            />
          }
        />
      </ConfigFieldControl>
      <ConfigFieldControl>
        <FormControlLabel
          label='Hide Support Users'
          control={
            <Checkbox
              color='primary'
              checked={!!element.config?.hideSupportUsers}
              onChange={() => {
                element.config!.hideSupportUsers =
                  !element.config!.hideSupportUsers;
                setFormState({ ...formState, selectedFormElement: element });
                notifyFormEdit();
              }}
            />
          }
        />
      </ConfigFieldControl>
      <ConfigFieldControl>
        <GroupPicker
          multiple
          value={element.config?.filterGroupIds || []}
          textFieldProps={{
            label: undefined,
            size: undefined,
            placeholder: 'Filter Groups'
          }}
          onChange={groups => {
            element.config!.filterGroupIds = groups.map(group => group.id);
            setFormState({ ...formState, selectedFormElement: element });
            notifyFormEdit();
          }}
        />
      </ConfigFieldControl>
    </>
  );
};

export const FormUserPickerPlugin: FormElementPlugin<
  any,
  FormUserPickerConfig
> = {
  icon: <Person />,
  name: 'User Picker',
  type: 'UserPicker',
  dataTypes: ['integer'],
  configurator: FormConfigUserPicker,
  element: FormUserPicker
};
